export interface BootstrapWorkspaceRef {
  id: string
  name: string | null
  /** Digital employees hired into the workspace, e.g. "gtm". */
  employees: Array<string>
}

export type WorkspaceEntryRoute =
  | '/$lang/workspace/$workspaceId'
  | '/$lang/workspace/$workspaceId/gtm'

function getWorkspaceRef(value: unknown): BootstrapWorkspaceRef | null {
  if (typeof value !== 'object' || value === null) {
    return null
  }

  const candidate = value as Record<string, unknown>
  if (typeof candidate.id !== 'string') {
    return null
  }

  return {
    id: candidate.id,
    name: typeof candidate.name === 'string' ? candidate.name : null,
    employees: Array.isArray(candidate.employees)
      ? candidate.employees.filter(
          (item): item is string => typeof item === 'string',
        )
      : [],
  }
}

export function getBootstrapWorkspaceRefs(
  workspaces: Array<unknown> | undefined,
): Array<BootstrapWorkspaceRef> {
  if (!workspaces) {
    return []
  }

  return workspaces
    .map(getWorkspaceRef)
    .filter(
      (workspace): workspace is BootstrapWorkspaceRef => workspace !== null,
    )
}

/** Workspaces holding the GTM employee: the GTM product's entry points. */
export function getGtmWorkspaceRefs(
  workspaces: Array<unknown> | undefined,
): Array<BootstrapWorkspaceRef> {
  return getBootstrapWorkspaceRefs(workspaces).filter((workspace) =>
    workspace.employees.includes('gtm'),
  )
}

export function getFirstBootstrapWorkspaceId(
  workspaces: Array<unknown> | undefined,
): string | null {
  return getBootstrapWorkspaceRefs(workspaces)[0]?.id ?? null
}

export function hasBootstrapWorkspaceId(
  workspaces: Array<unknown> | undefined,
  workspaceId: string,
): boolean {
  return getBootstrapWorkspaceRefs(workspaces).some(
    (workspace) => workspace.id === workspaceId,
  )
}

export function getBootstrapWorkspace(
  workspaces: Array<unknown> | undefined,
  workspaceId: string,
): BootstrapWorkspaceRef | null {
  return (
    getBootstrapWorkspaceRefs(workspaces).find(
      (workspace) => workspace.id === workspaceId,
    ) ?? null
  )
}

export function getWorkspaceEntryRoute(
  workspace: BootstrapWorkspaceRef,
): WorkspaceEntryRoute {
  return workspace.employees.includes('gtm')
    ? '/$lang/workspace/$workspaceId/gtm'
    : '/$lang/workspace/$workspaceId'
}

export function getPreferredBootstrapWorkspaceId(
  workspaces: Array<unknown> | undefined,
  lastWorkspaceId: string | null,
): string | null {
  const refs = getBootstrapWorkspaceRefs(workspaces)
  const firstWorkspaceId = refs[0]?.id ?? null

  if (!lastWorkspaceId) {
    return firstWorkspaceId
  }

  return refs.some((workspace) => workspace.id === lastWorkspaceId)
    ? lastWorkspaceId
    : firstWorkspaceId
}
